import { forwardRef, type InputHTMLAttributes } from "react";
import { Check } from "lucide-react";
import { cn } from "../cn";

/* 1:1 with discordforge.org — WebhooksV2Manager.tsx checkbox:
 *   <input className="peer sr-only" /> + span with
 *   border-[1.5px] border-white/20 bg-black/20
 *   peer-checked:border-orange-400
 *   peer-checked:bg-[linear-gradient(135deg,#ff9500,#ff6b00)]
 *   peer-checked:shadow-[0_0_12px_rgba(255,140,0,0.4)] */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
    label?: React.ReactNode;
    /** Label placement — right (default) or left */
    labelSide?: "right" | "left";
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, labelSide = "right", className, disabled, id, ...rest }, ref) => {
        const inner = (
            <>
                <input
                    ref={ref}
                    id={id}
                    type="checkbox"
                    disabled={disabled}
                    className="peer sr-only"
                    {...rest}
                />
                <span
                    aria-hidden
                    className={cn(
                        "flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] border-white/20 bg-black/20 transition duration-150",
                        "peer-checked:border-orange-400 peer-checked:bg-[linear-gradient(135deg,#ff9500,#ff6b00)]",
                        "peer-checked:shadow-[0_0_12px_rgba(255,140,0,0.4)]",
                        "peer-focus-visible:ring-2 peer-focus-visible:ring-orange-400/50",
                        "peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                    )}
                >
                    <Check
                        size={12}
                        strokeWidth={3.5}
                        className="text-black opacity-0 transition-opacity duration-150 peer-checked:opacity-100"
                    />
                </span>
            </>
        );

        if (!label) {
            return (
                <label className={cn("inline-flex cursor-pointer items-center", disabled && "cursor-not-allowed", className)}>
                    {inner}
                </label>
            );
        }

        return (
            <label
                htmlFor={id}
                className={cn(
                    "inline-flex cursor-pointer select-none items-center gap-2.5 text-sm font-medium text-zinc-200",
                    disabled && "cursor-not-allowed opacity-60",
                    className
                )}
            >
                {labelSide === "left" && <span>{label}</span>}
                {inner}
                {labelSide === "right" && <span>{label}</span>}
            </label>
        );
    }
);

Checkbox.displayName = "Checkbox";
